import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import './ErrorMarginArticle.css';

interface Source {
  id: string;
  name: string;
  type: string;
  url: string;
}

const sources: Source[] = [
  { id: 'S1', name: 'How to Choose a Threshold for an Evaluation Metric for LLMs (arxiv, 2024)', type: 'Academic paper', url: 'https://arxiv.org/html/2412.12148v1' },
  { id: 'S2', name: 'Anthropic — Demystifying Evals for AI Agents', type: 'Lab guide', url: 'https://www.anthropic.com/engineering/demystifying-evals-for-ai-agents' },
  { id: 'S3', name: 'Anthropic — Advancing Claude in Healthcare', type: 'Lab announcement', url: 'https://www.anthropic.com/news/healthcare-life-sciences' },
  { id: 'S4', name: 'Anthropic — Statistical Approach to Model Evals', type: 'Lab research', url: 'https://www.anthropic.com/research/statistical-approach-to-model-evals' },
  { id: 'S5', name: 'Microsoft — Content Filter Severity Levels', type: 'Platform docs', url: 'https://learn.microsoft.com/en-us/azure/ai-foundry/openai/concepts/content-filter-severity-levels?view=foundry-classic' },
  { id: 'S6', name: 'Microsoft — How to Evaluate LLMs: A Complete Metric Framework', type: 'Lab guide', url: 'https://www.microsoft.com/en-us/research/articles/how-to-evaluate-llms-a-complete-metric-framework/' },
  { id: 'S7', name: 'Microsoft — Content Risk Mitigation Strategies', type: 'Platform docs', url: 'https://learn.microsoft.com/en-us/azure/ai-studio/concepts/evaluation-improvement-strategies' },
  { id: 'S8', name: 'Confident AI — The Ultimate LLM Evaluation Playbook', type: 'Framework guide', url: 'https://www.confident-ai.com/blog/the-ultimate-llm-evaluation-playbook' },
  { id: 'S9', name: 'Confident AI — RAG Evaluation Metrics', type: 'Framework guide', url: 'https://www.confident-ai.com/blog/rag-evaluation-metrics-answer-relevancy-faithfulness-and-more' },
  { id: 'S10', name: 'Hamel Husain — LLM Evals: Everything You Need to Know', type: 'Practitioner', url: 'https://hamel.dev/blog/posts/evals-faq/' },
  { id: 'S11', name: "Judge's Verdict: LLM Judge Capability Through Human Agreement (arxiv, 2025)", type: 'Academic paper', url: 'https://arxiv.org/abs/2510.09738v1' },
  { id: 'S12', name: 'Galileo — Enhancing AI Evaluation with Cohen\'s Kappa', type: 'Framework guide', url: 'https://galileo.ai/blog/cohens-kappa-metric' },
];

const ErrorMarginArticle: React.FC = () => {
  const [sourcesOpen, setSourcesOpen] = useState(false);

  return (
    <>
      <div className="margin-article-page">
        <div className="margin-article-container">
          <Link to="/wiki" className="back-link">← Back to Wiki</Link>

          <header className="margin-article-header">
            <h1>Finding Your Acceptable Error Margin</h1>
            <p className="margin-article-subtitle">A Practical Guide for LLM Applications</p>
            <p className="margin-article-description">
              Neither Anthropic nor Microsoft will tell you what eval score is "good enough" for your
              application. This is deliberate — acceptable error margins depend on your domain, your
              risk tolerance, and your users. This guide walks through the process of finding yours.
            </p>
          </header>

          {/* Quick Steps Overview */}
          <section className="margin-quick-steps">
            <div className="margin-quick-steps-card">
              <h2 className="margin-quick-steps-title">The 7-Step Process</h2>
              <ol className="margin-quick-steps-list">
                <li><strong>Classify your risk tier</strong> — determines whether you need thresholds or human oversight</li>
                <li><strong>Label your ground truth</strong> — 25–50 real examples marked good/bad by domain experts</li>
                <li><strong>Calibrate your judge</strong> — measure judge vs human agreement (target Kappa &gt;0.7)</li>
                <li><strong>Derive thresholds empirically</strong> — find where "good" and "bad" score distributions separate</li>
                <li><strong>Set two quality gates</strong> — regression gates (protect current quality) + capability gates (push improvement)</li>
                <li><strong>Apply statistical methods</strong> — for high-stakes, use conformal prediction instead of eyeballing</li>
                <li><strong>Re-calibrate when things change</strong> — new model, new prompt, new retrieval = new thresholds</li>
              </ol>
            </div>
          </section>

          {/* Sources */}
          <section className="margin-sources-section">
            <button
              className="margin-sources-toggle"
              onClick={() => setSourcesOpen(!sourcesOpen)}
              aria-expanded={sourcesOpen}
            >
              <span className="margin-sources-toggle-icon">{sourcesOpen ? '−' : '+'}</span>
              <span>Sources ({sources.length} sources)</span>
            </button>
            {sourcesOpen && (
              <div className="margin-sources-table-wrapper">
                <table className="margin-sources-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Source</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((source) => (
                      <tr key={source.id}>
                        <td className="margin-source-id">{source.id}</td>
                        <td>
                          <a href={source.url} target="_blank" rel="noopener noreferrer">
                            {source.name}
                          </a>
                        </td>
                        <td className="margin-source-type">{source.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Why no universal answer */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Context</span>
              Why There Is No Universal "Good" Score
            </h2>
            <div className="margin-callout">
              <p>
                Every major LLM provider — Anthropic, Microsoft, Google, OpenAI — deliberately avoids
                publishing industry-specific score thresholds. This is not laziness. There are three
                structural reasons:
              </p>
            </div>
            <div className="margin-reason-cards">
              <div className="margin-reason-card">
                <h4>Scores aren't calibrated across tools</h4>
                <p>
                  A faithfulness score of 0.85 from RAGAS means something different than 0.85 from
                  DeepEval. There is no standardization. Even within a single tool, score distributions
                  shift when you change the judge model.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S1</span>
                  <span className="margin-source-pill">S8</span>
                </div>
              </div>
              <div className="margin-reason-card">
                <h4>Risk tolerance varies by orders of magnitude</h4>
                <p>
                  A movie recommendation chatbot and a medical dosage calculator both produce text. A 5%
                  error rate is fine for one and potentially lethal for the other. No single number serves both.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S3</span>
                  <span className="margin-source-pill">S5</span>
                </div>
              </div>
              <div className="margin-reason-card">
                <h4>The metrics themselves are noisy</h4>
                <p>
                  LLM-as-a-Judge agrees with humans roughly 80% of the time. You're setting a threshold on
                  a measurement with ~20% uncertainty. A threshold of 0.85 vs 0.80 may be within the
                  noise floor of your evaluator.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S4</span>
                  <span className="margin-source-pill">S11</span>
                </div>
              </div>
            </div>
          </section>

          {/* Step 1: Risk Tier */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Step 1</span>
              Classify Your Risk Tier
            </h2>
            <p className="margin-section-intro">
              Before thinking about numbers, determine what kind of oversight your application needs.
              This decision shapes everything downstream — whether you need thresholds at all, or
              whether human review is the answer regardless of score.
            </p>
            <div className="margin-tier-table-wrapper">
              <table className="margin-tier-table">
                <thead>
                  <tr>
                    <th>Risk Tier</th>
                    <th>Examples</th>
                    <th>Error Approach</th>
                    <th>Sources</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="margin-tier-name tier-low">Autonomous OK</td>
                    <td>Content recommendations, search, internal summarization, creative writing</td>
                    <td>Set metric thresholds, automate deployment decisions</td>
                    <td className="margin-tier-sources">S6, S7</td>
                  </tr>
                  <tr>
                    <td className="margin-tier-name tier-medium">Human Review on Failures</td>
                    <td>Customer support, code generation, document drafting, data analysis</td>
                    <td>Set thresholds, route failures to human reviewers</td>
                    <td className="margin-tier-sources">S2, S6</td>
                  </tr>
                  <tr>
                    <td className="margin-tier-name tier-high">Human Review on Everything</td>
                    <td>Healthcare diagnosis, legal advice, financial decisions, hiring</td>
                    <td>AI drafts, human always decides — score thresholds are secondary to oversight</td>
                    <td className="margin-tier-sources">S3, S5</td>
                  </tr>
                  <tr>
                    <td className="margin-tier-name tier-prohibited">Don't Automate</td>
                    <td>Autonomous weapons, sentencing without appeal, unsupervised medical dosing</td>
                    <td>Excluded by usage policies of Anthropic, Microsoft, OpenAI, and Google</td>
                    <td className="margin-tier-sources">S3</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="margin-callout margin-callout-key">
              <p>
                <strong>Key insight:</strong> The threshold question only matters for the first two tiers. For
                "Human Review on Everything" applications, the answer is not "what score is acceptable" — it's
                "a human must always verify, regardless of score."
              </p>
              <p>
                Anthropic's Claude for Healthcare demonstrates this: despite scoring 91–94% on MedQA,
                their deployment model requires biostatistician validation, clinical expert review, and
                IRB approval for every use. The AI proposes, the human decides.
              </p>
              <div className="margin-source-pills">
                <span className="margin-source-pill">S3</span>
              </div>
            </div>
          </section>

          {/* Step 2: Label Ground Truth */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Step 2</span>
              Label Your Ground Truth
            </h2>
            <p className="margin-section-intro">
              Start with human labels, not scores. This is the step most teams skip, and it's the
              reason their thresholds are arbitrary.
            </p>
            <div className="margin-process-cards">
              <div className="margin-process-card">
                <div className="margin-process-number">2a</div>
                <h4>Collect 25–50 real examples</h4>
                <p>
                  Pull actual queries your system has handled (or will handle). Include both easy and hard cases.
                  Anthropic recommends drawing from real failures — 20–50 tasks drawn from observed
                  issues is enough to start.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S2</span>
                  <span className="margin-source-pill">S8</span>
                  <span className="margin-source-pill">S10</span>
                </div>
              </div>
              <div className="margin-process-card">
                <div className="margin-process-number">2b</div>
                <h4>Label each as "good" or "bad"</h4>
                <p>
                  Have domain experts (not the dev team) label each response. Use binary pass/fail — not
                  numeric scores. The goal is ground truth on what's acceptable for <em>your</em> application.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S8</span>
                  <span className="margin-source-pill">S10</span>
                </div>
              </div>
              <div className="margin-process-card">
                <div className="margin-process-number">2c</div>
                <h4>Measure inter-annotator agreement</h4>
                <p>
                  If multiple people label the same examples, measure how often they agree. If humans
                  disagree on what "good" means, no metric threshold will help — you have a requirements
                  problem, not a metrics problem.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S10</span>
                  <span className="margin-source-pill">S11</span>
                </div>
              </div>
            </div>
          </section>

          {/* Step 3: Calibrate Judge */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Step 3</span>
              Calibrate Your Judge Against Humans
            </h2>
            <p className="margin-section-intro">
              Run your automated metrics (LLM-as-a-Judge, RAGAS, DeepEval, etc.) on the same labeled
              dataset. Measure how well the judge agrees with human labels. If the judge is unreliable,
              no threshold you set on its scores will be meaningful.
            </p>
            <div className="margin-agreement-table-wrapper">
              <table className="margin-agreement-table">
                <thead>
                  <tr>
                    <th>Cohen's Kappa</th>
                    <th>Interpretation</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="margin-kappa-value kappa-bad">&lt; 0.40</td>
                    <td>Fair or worse</td>
                    <td>Judge is unreliable. Fix the judge prompt, switch models, or redesign the metric before proceeding.</td>
                  </tr>
                  <tr>
                    <td className="margin-kappa-value kappa-ok">0.40 – 0.60</td>
                    <td>Moderate</td>
                    <td>Usable for coarse filtering. Don't trust fine-grained score differences.</td>
                  </tr>
                  <tr>
                    <td className="margin-kappa-value kappa-good">0.60 – 0.80</td>
                    <td>Substantial</td>
                    <td>Good enough for most production use. This is the practical target.</td>
                  </tr>
                  <tr>
                    <td className="margin-kappa-value kappa-great">&gt; 0.80</td>
                    <td>Near-perfect</td>
                    <td>Human-level agreement. Top LLM judges achieve 0.78–0.82 on established benchmarks.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="margin-callout">
              <p>
                <strong>Target:</strong> Aim for &lt;5% combined false positive + false negative rate when
                your judge's pass/fail calls are compared against human labels. If the judge passes things
                humans would fail (or vice versa) more than 5% of the time, iterate on the judge before
                trusting its scores.
              </p>
              <div className="margin-source-pills">
                <span className="margin-source-pill">S8</span>
                <span className="margin-source-pill">S10</span>
                <span className="margin-source-pill">S11</span>
                <span className="margin-source-pill">S12</span>
              </div>
            </div>
          </section>

          {/* Step 4: Derive Thresholds */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Step 4</span>
              Derive Thresholds Empirically
            </h2>
            <p className="margin-section-intro">
              Now — and only now — look at what scores the "good" examples get vs the "bad" ones.
              The threshold is wherever those distributions separate.
            </p>
            <div className="margin-process-cards">
              <div className="margin-process-card">
                <div className="margin-process-number">4a</div>
                <h4>Plot score distributions</h4>
                <p>
                  Run your metrics on all labeled examples. Plot the scores for "good" and "bad" examples
                  separately. In practice, faithfulness scores tend to be bimodal — clustered near 0 and 1 —
                  not normally distributed. Standard methods like z-scores fail on these distributions.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S1</span>
                </div>
              </div>
              <div className="margin-process-card">
                <div className="margin-process-number">4b</div>
                <h4>Find the separation point</h4>
                <p>
                  The threshold goes where the "good" and "bad" distributions overlap the least. For simple
                  cases, eyeball it. For production systems, use conformal prediction (see Step 6).
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S1</span>
                </div>
              </div>
              <div className="margin-process-card">
                <div className="margin-process-number">4c</div>
                <h4>Validate on held-out data</h4>
                <p>
                  Split your labeled set. Derive the threshold on one half, test it on the other.
                  If it doesn't generalize, you need more data or a better metric.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S1</span>
                  <span className="margin-source-pill">S8</span>
                </div>
              </div>
            </div>
          </section>

          {/* Step 5: Two Types of Gates */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Step 5</span>
              Set Two Types of Quality Gates
            </h2>
            <p className="margin-section-intro">
              Anthropic's agent evaluation guide distinguishes two fundamentally different kinds of
              eval thresholds. You need both.
            </p>
            <div className="margin-gates-grid">
              <div className="margin-gate-card gate-regression">
                <h3>Regression Gates</h3>
                <p className="margin-gate-subtitle">Protect what already works</p>
                <ul>
                  <li>Your current best score minus a small margin</li>
                  <li>Should have near-100% pass rate</li>
                  <li>If scores drop below this, <strong>something is broken</strong> — investigate immediately</li>
                  <li>Run on every deployment, every prompt change, every model swap</li>
                </ul>
                <p className="margin-gate-example">
                  Example: Your chatbot currently scores 0.92 faithfulness. Set a regression gate at 0.88.
                  If a new prompt template drops you to 0.85, the gate catches it.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S2</span>
                  <span className="margin-source-pill">S7</span>
                </div>
              </div>
              <div className="margin-gate-card gate-capability">
                <h3>Capability Gates</h3>
                <p className="margin-gate-subtitle">Push what doesn't work yet</p>
                <ul>
                  <li>Aspirationally hard tests — your "hill to climb"</li>
                  <li>50–70% pass rate is healthy and expected</li>
                  <li>If you're passing 100%, <strong>your evals are too easy</strong></li>
                  <li>These track improvement over time, not deployment readiness</li>
                </ul>
                <p className="margin-gate-example">
                  Example: Your chatbot can't handle multi-hop reasoning questions. Add those as capability
                  evals. Today: 40% pass rate. Target: 70% in 3 months.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S2</span>
                  <span className="margin-source-pill">S10</span>
                </div>
              </div>
            </div>
            <div className="margin-callout margin-callout-key">
              <p>
                <strong>Hamel Husain:</strong> "If you're passing 100% of your evals, you're likely not
                challenging your system enough. A 70% pass rate might indicate a more meaningful evaluation
                that's actually stress-testing your application."
              </p>
              <div className="margin-source-pills">
                <span className="margin-source-pill">S10</span>
              </div>
            </div>
          </section>

          {/* Step 6: Statistical Methods */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Step 6</span>
              Use Statistical Methods for High-Stakes Decisions
            </h2>
            <p className="margin-section-intro">
              For applications where the threshold really matters (deployment gates, safety filters), use
              rigorous statistical methods instead of eyeballing. Research from 2024 tested multiple
              approaches on the HaluBench dataset and found significant differences in reliability.
            </p>
            <div className="margin-stats-table-wrapper">
              <table className="margin-stats-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>How It Works</th>
                    <th>Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="margin-method-name">Z-Score</td>
                    <td>Standard deviations from mean score</td>
                    <td className="margin-verdict-bad">Fails — assumes normal distribution, but eval scores are bimodal</td>
                  </tr>
                  <tr>
                    <td className="margin-method-name">Kernel Density Estimation (KDE)</td>
                    <td>Finds peaks in score distribution, sets threshold at midpoint</td>
                    <td className="margin-verdict-ok">Adequate at low confidence (80%), defaults to zero at higher levels</td>
                  </tr>
                  <tr>
                    <td className="margin-method-name">Empirical Recall</td>
                    <td>Varies threshold to build recall curves</td>
                    <td className="margin-verdict-ok">Sound but overly conservative at high confidence</td>
                  </tr>
                  <tr>
                    <td className="margin-method-name">AUC-ROC with Classifiers</td>
                    <td>Trains logistic regression or decision tree to find optimal split</td>
                    <td className="margin-verdict-ok">Good, but requires careful classifier selection</td>
                  </tr>
                  <tr>
                    <td className="margin-method-name">Conformal Prediction</td>
                    <td>Model-agnostic prediction intervals at specified confidence levels</td>
                    <td className="margin-verdict-good">Best performer — guaranteed coverage with minimal prediction set width</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="margin-callout">
              <p>
                <strong>Recommendation:</strong> For high-stakes deployment gates, use conformal prediction
                with a GAM or polynomial logistic regression. For everything else, empirical recall curves
                or simple percentile analysis on your labeled data is sufficient.
              </p>
              <div className="margin-source-pills">
                <span className="margin-source-pill">S1</span>
              </div>
            </div>
          </section>

          {/* Case Studies */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Reference</span>
              How the Big Players Handle It
            </h2>
            <p className="margin-section-intro">
              Neither Anthropic nor Microsoft publishes "acceptable scores by industry." But their
              products reveal their thinking.
            </p>
            <div className="margin-case-studies">
              <div className="margin-case-card">
                <h3>Microsoft Azure AI Content Safety</h3>
                <p className="margin-case-approach">Approach: configurable severity levels, context-dependent defaults</p>
                <p>
                  Microsoft defines 4 severity levels (Safe, Low, Medium, High) across 4 risk categories
                  (hate, sexual, violence, self-harm). The default filters at <strong>Medium</strong> severity,
                  meaning Low-severity content is allowed through.
                </p>
                <p>
                  Their key guidance: a children's app should filter more aggressively than an adult one.
                  Medical and research contexts are permitted at higher severity levels. Fictional contexts
                  (games, movies) are rated lower severity. But they won't pick the threshold for you —
                  they say <em>"conduct your own testing to ensure effectiveness for your application."</em>
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S5</span>
                  <span className="margin-source-pill">S7</span>
                </div>
              </div>
              <div className="margin-case-card">
                <h3>Anthropic Claude for Healthcare</h3>
                <p className="margin-case-approach">Approach: high accuracy + mandatory human oversight, no autonomous deployment</p>
                <p>
                  Claude achieves 91–94% on MedQA and 61.3% on MedCalc (complex multi-step medical
                  calculations). Their response to the 61.3% figure is not "that's acceptable" — it's
                  "human oversight is required."
                </p>
                <p>
                  The deployment model: Claude <strong>proposes</strong>, humans <strong>decide</strong>.
                  Every use requires biostatistician validation, clinical expert review, and IRB approval.
                  Their usage policy explicitly requires <em>"a qualified professional review content
                  or decisions"</em> for healthcare, legal, and financial use cases.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S3</span>
                </div>
              </div>
              <div className="margin-case-card">
                <h3>Anthropic's Paired-Differences Approach</h3>
                <p className="margin-case-approach">Approach: statistical rigor for model comparisons</p>
                <p>
                  Rather than setting absolute thresholds, Anthropic uses paired-differences tests to
                  determine if one model or prompt is statistically better than another. This sidesteps
                  the "what's a good score" question entirely — you instead ask "is this change an
                  improvement?" with confidence intervals.
                </p>
                <div className="margin-source-pills">
                  <span className="margin-source-pill">S4</span>
                </div>
              </div>
            </div>
          </section>

          {/* Starting Points */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Reference</span>
              Rough Starting Points
            </h2>
            <p className="margin-section-intro">
              These are commonly cited starting points — not standards. Use them as initial values
              to test against your labeled data, not as targets to aim for. Your empirically derived
              thresholds (Step 4) override these.
            </p>
            <div className="margin-starting-table-wrapper">
              <table className="margin-starting-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Low-Stakes</th>
                    <th>Standard Production</th>
                    <th>High-Stakes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Faithfulness</td>
                    <td>&gt;0.7</td>
                    <td>&gt;0.85</td>
                    <td>&gt;0.9 + human review</td>
                  </tr>
                  <tr>
                    <td>Answer Relevancy</td>
                    <td>&gt;0.6</td>
                    <td>&gt;0.8</td>
                    <td>&gt;0.9 + human review</td>
                  </tr>
                  <tr>
                    <td>Context Precision</td>
                    <td>&gt;0.5</td>
                    <td>&gt;0.7</td>
                    <td>&gt;0.85</td>
                  </tr>
                  <tr>
                    <td>Judge–Human Agreement (Kappa)</td>
                    <td>&gt;0.6</td>
                    <td>&gt;0.7</td>
                    <td>&gt;0.8</td>
                  </tr>
                  <tr>
                    <td>Combined FP+FN Rate</td>
                    <td>&lt;10%</td>
                    <td>&lt;5%</td>
                    <td>&lt;2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="margin-source-pills" style={{ marginTop: '1rem' }}>
              <span className="margin-source-pill">S8</span>
              <span className="margin-source-pill">S9</span>
              <span className="margin-source-pill">S11</span>
            </div>
          </section>

          {/* Re-calibration */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Step 7</span>
              Re-Calibrate When Things Change
            </h2>
            <p className="margin-section-intro">
              Your thresholds are not permanent. Re-derive them whenever:
            </p>
            <div className="margin-recalibrate-list">
              <div className="margin-recalibrate-item">
                <span className="margin-recalibrate-trigger">You change the LLM model</span>
                <span className="margin-recalibrate-why">Score distributions shift between models</span>
              </div>
              <div className="margin-recalibrate-item">
                <span className="margin-recalibrate-trigger">You change the judge model</span>
                <span className="margin-recalibrate-why">Different judges calibrate differently</span>
              </div>
              <div className="margin-recalibrate-item">
                <span className="margin-recalibrate-trigger">You change the prompt or retrieval strategy</span>
                <span className="margin-recalibrate-why">New failure modes may emerge</span>
              </div>
              <div className="margin-recalibrate-item">
                <span className="margin-recalibrate-trigger">You see production failures your evals missed</span>
                <span className="margin-recalibrate-why">Add those failures to your labeled set</span>
              </div>
              <div className="margin-recalibrate-item">
                <span className="margin-recalibrate-trigger">User complaints shift to a new category</span>
                <span className="margin-recalibrate-why">Your error taxonomy has changed</span>
              </div>
            </div>
            <div className="margin-source-pills" style={{ marginTop: '1rem' }}>
              <span className="margin-source-pill">S2</span>
              <span className="margin-source-pill">S6</span>
              <span className="margin-source-pill">S10</span>
            </div>
          </section>

          {/* Summary */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">Summary</span>
              The Process
            </h2>
            <div className="margin-summary-steps">
              <div className="margin-summary-step">
                <span className="margin-summary-num">1</span>
                <span>Classify your risk tier — this determines whether you need thresholds or human oversight</span>
              </div>
              <div className="margin-summary-step">
                <span className="margin-summary-num">2</span>
                <span>Label 25–50 real examples as good/bad by hand with domain experts</span>
              </div>
              <div className="margin-summary-step">
                <span className="margin-summary-num">3</span>
                <span>Calibrate your judge — target Kappa &gt;0.7 and &lt;5% combined FP+FN rate</span>
              </div>
              <div className="margin-summary-step">
                <span className="margin-summary-num">4</span>
                <span>Derive thresholds from where "good" and "bad" score distributions separate</span>
              </div>
              <div className="margin-summary-step">
                <span className="margin-summary-num">5</span>
                <span>Set regression gates (protect current quality) and capability gates (push improvement)</span>
              </div>
              <div className="margin-summary-step">
                <span className="margin-summary-num">6</span>
                <span>For high-stakes, use conformal prediction instead of eyeballing</span>
              </div>
              <div className="margin-summary-step">
                <span className="margin-summary-num">7</span>
                <span>Re-calibrate whenever you change models, prompts, or retrieval</span>
              </div>
            </div>
          </section>

          {/* Cross-references */}
          <section className="margin-section">
            <h2 className="margin-section-heading">
              <span className="margin-step-badge">See Also</span>
              Related Articles
            </h2>
            <div className="margin-related-links">
              <Link to="/wiki/eval-methods" className="margin-related-link">
                <span className="margin-related-title">LLM Evaluation Methods</span>
                <span className="margin-related-desc">45 methods ranked by citation count — the techniques these thresholds apply to</span>
              </Link>
              <Link to="/wiki/rag-eval" className="margin-related-link">
                <span className="margin-related-title">RAG Evaluation Methods</span>
                <span className="margin-related-desc">23 metrics across retrieval, generation, and diagnostics — the specific scores you're thresholding</span>
              </Link>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ErrorMarginArticle;
